import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  APP_STORAGE_CHANGE_EVENT,
  clearCoachRecordingsUnreadAlert,
  clearUsersReportsUnreadAlert,
  getCoachRecordingsHasUnreadAlert,
  getGlobalNotificationsEnabled,
  getLoggedInRoleKey,
  getUsersReportsHasUnreadAlert,
  setGlobalNotificationsEnabled,
} from "../../utils/browserStorage.util";
import Logout from "./Logout";
import {
  FaBriefcase,
  FaUsers,
  FaComments,
  FaMoneyBill,
  FaClipboardList,
  FaTicketAlt,
  FaExchangeAlt,
  FaVideo,
  FaClock,
  FaClipboardCheck,
  FaDoorOpen,
  FaCreditCard,
  FaHandshake,
  FaChartBar,
  FaNewspaper,
  FaRobot,
  FaStar,
  FaChevronDown,
  FaChevronRight,
  FaBars,
  FaTimes,
  FaMicrophone,
  FaTags,
  FaVolumeUp,
  FaFileAlt,
  FaUserSecret,
  FaExclamationTriangle,
  FaMoon,
  FaSun,
  FaBook,
  FaBullhorn,
  FaHeartbeat,
} from "react-icons/fa";
import { IoNotifications, IoNotificationsOff } from "react-icons/io5";
import { useTheme } from "src/context/theme.context";

// Define UserRole enum or constant
export enum UserRole {
  ADMIN = "ADMIN",
  // Add other roles if needed
}

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const SideBar = ({ isCollapsed, onToggle }: SidebarProps) => {
  const role = getLoggedInRoleKey();
  const isAdmin = role === "admin";
  const isEmployee = role === "employee";
  const { pathname } = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const [notificationsEnabled, setNotificationsEnabledState] = useState(getGlobalNotificationsEnabled);
  const [hasUnreadCoachRecordings, setHasUnreadCoachRecordings] = useState(getCoachRecordingsHasUnreadAlert);
  const [hasUnreadUsersReports, setHasUnreadUsersReports] = useState(getUsersReportsHasUnreadAlert);
  
  // State for collapsible sections - auto-expand analytics for employees
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    analytics: isEmployee, // Auto-expand for employees
    transactions: false,
    management: false,
    support: false,
    "technical-reports": false,
  });

  // Helper function to toggle section
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleNotifications = () => {
    setNotificationsEnabledState((prev) => {
      const nextValue = !prev;
      setGlobalNotificationsEnabled(nextValue);
      return nextValue;
    });
  };

  useEffect(() => {
    if (pathname.startsWith("/coach-records")) {
      clearCoachRecordingsUnreadAlert();
      setHasUnreadCoachRecordings(false);
    }

    if (pathname.startsWith("/users-reports")) {
      clearUsersReportsUnreadAlert();
      setHasUnreadUsersReports(false);
    }
  }, [pathname]);

  useEffect(() => {
    const syncCoachRecordingsAlert = () => {
      setHasUnreadCoachRecordings(getCoachRecordingsHasUnreadAlert());
    };

    const syncUsersReportsAlert = () => {
      setHasUnreadUsersReports(getUsersReportsHasUnreadAlert());
    };

    const handleAppStorageChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ key?: string }>;
      if (customEvent.detail?.key === "coachRecordingsHasUnreadAlert") {
        syncCoachRecordingsAlert();
      }
      if (customEvent.detail?.key === "usersReportsHasUnreadAlert") {
        syncUsersReportsAlert();
      }
    };

    const handleStorage = () => {
      syncCoachRecordingsAlert();
      syncUsersReportsAlert();
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener(APP_STORAGE_CHANGE_EVENT, handleAppStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(APP_STORAGE_CHANGE_EVENT, handleAppStorageChange);
    };
  }, []);

  // Enhanced route configuration with active state logic
  const routeConfig = {
    // Exact match routes (no sub-routes)
    exact: [
      "/statistics",
      "/graphical-statistics",
      "/chat-support",
      "/recruiter",
      "/withdrawal-requests",
      "/karma-exchanges",
      "/rewards",
      "/coach-records",
      "/credit-vouchers",
      "/users-transactions",
      "/payment-transactions",
      "/billing-exceptions",
      "/bookings",
      "/ai-sessions",
      "/word-bank",
      "/ai-coach/prompts",
      "/ai-coach/level-vocabulary",
      "/ai-coach/topics",
      "/ai-coach/custom-topics",
      "/ai-coach/voices",
      "/subscription-users",
      "/slots",
      "/slot-requests",
      "/active-slots-reports",
      "/lounge-requests",
      "/onboarding-reports",
      "/users-reports",
      "/function-errors",
      "/task-failures",
      "/audience-health",
      "/integration-health",
      "/admin-email",
      "/coach-signup-attribution",
    ],
    // Sub-route aware routes (can have :id parameters)
    subRoutes: ["/users", "/promoters", "/sessions", "/rooms", "/karma-exchanges", "/rewards", "/recruiter"],
    // Special cases with custom logic
    special: {
      "/users": {
        // Users should be active for /users and /users/:id but not /users-transactions
        test: (path: string) =>
          path === "/users" || (path.startsWith("/users/") && !path.startsWith("/users-transactions")),
      },
    },
  };

  // Enhanced helper function to determine if a link should be active
  const isLinkActive = (linkPath: string): boolean => {
    // Check exact match first
    if (pathname === linkPath) return true;

    // Check special cases
    if (linkPath === "/users" && routeConfig.special["/users"]) {
      return routeConfig.special["/users"].test(pathname);
    }

    // Check sub-route aware routes
    if (routeConfig.subRoutes.includes(linkPath)) {
      return pathname.startsWith(linkPath + "/");
    }

    // Check exact match routes
    if (routeConfig.exact.includes(linkPath)) {
      return false; // Already checked exact match above
    }

    // Default: exact matching only
    return false;
  };

  // Organized navigation sections
  const navigationSections = [
    {
      id: "analytics",
      title: "Analytics",
      icon: <FaChartBar className="w-4 h-4 text-gray-300" />,
      items: [
        {
          to: "/statistics",
          label: "Statistics",
          icon: <FaChartBar className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/graphical-statistics",
          label: "Graphical Statistics",
          icon: <FaChartBar className="w-4 h-4 text-gray-300" />,
        },
      ],
    },
    {
      id: "transactions",
      title: "Transactions",
      icon: <FaExchangeAlt className="w-4 h-4 text-gray-300" />,
      items: [
        {
          to: "/withdrawal-requests",
          label: "Withdrawal Requests",
          icon: <FaMoneyBill className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/karma-exchanges",
          label: "Karma Exchanges",
          icon: <FaHandshake className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/rewards",
          label: "Rewards Management",
          icon: <FaTicketAlt className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/users-transactions",
          label: "Users Transactions",
          icon: <FaExchangeAlt className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/payment-transactions",
          label: "Payment Transactions",
          icon: <FaCreditCard className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/billing-exceptions",
          label: "Billing Exceptions",
          icon: <FaExclamationTriangle className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/credit-vouchers",
          label: "Credit Vouchers",
          icon: <FaTicketAlt className="w-4 h-4 text-gray-300" />,
        },
      ],
    },
    {
      id: "management",
      title: "Management",
      icon: <FaUsers className="w-4 h-4 text-gray-300" />,
      items: [
        {
          to: "/users",
          label: "Users",
          icon: <FaUsers className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/promoters",
          label: "Promoters",
          icon: <FaHandshake className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/sessions",
          label: "Sessions",
          icon: <FaVideo className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/bookings",
          label: "Bookings",
          icon: <FaClipboardCheck className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/ai-sessions",
          label: "AI Sessions",
          icon: <FaRobot className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/word-bank",
          label: "Word Bank",
          icon: <FaBook className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/subscription-users",
          label: "Subscription Users",
          icon: <FaStar className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/slots",
          label: "Slots",
          icon: <FaClock className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/slot-requests",
          label: "Slot Requests",
          icon: <FaClipboardList className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/rooms",
          label: "Rooms",
          icon: <FaMicrophone className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/coach-records",
          label: "Coach Records",
          icon: <FaClipboardList className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/coach-signup-attribution",
          label: "Coach Signup Attribution",
          icon: <FaBullhorn className="w-4 h-4 text-gray-300" />,
        },
      ],
    },
    {
      id: "support",
      title: "Support & Content",
      icon: <FaComments className="w-4 h-4 text-gray-300" />,
      items: [
        {
          to: "/chat-support",
          label: "Chat Support",
          icon: <FaComments className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/recruiter",
          label: "Recruiter Inbox",
          icon: <FaBriefcase className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/ai-coach/prompts",
          label: "AI Coach Prompts",
          icon: <FaFileAlt className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/ai-coach/level-vocabulary",
          label: "AI Coach Level Vocabulary",
          icon: <FaBook className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/ai-coach/topics",
          label: "AI Topics",
          icon: <FaTags className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/ai-coach/custom-topics",
          label: "AI Custom Topics",
          icon: <FaRobot className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/ai-coach/voices",
          label: "AI Coach Languages",
          icon: <FaVolumeUp className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/lounge-requests",
          label: "Lounge Requests",
          icon: <FaDoorOpen className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/feed",
          label: "Feed",
          icon: <FaNewspaper className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/admin-email",
          label: "Admin Email",
          icon: <FaBullhorn className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/onboarding-reports",
          label: "Onboarding Reports",
          icon: <FaClipboardList className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/users-reports",
          label: "Users Reports",
          icon: <FaUserSecret className="w-4 h-4 text-gray-300" />,
        },
      ],
    },
    {
      id: "technical-reports",
      title: "Technical Reports",
      icon: <FaFileAlt className="w-4 h-4 text-gray-300" />,
      items: [
        {
          to: "/active-slots-reports",
          label: "Active Slots Reports",
          icon: <FaFileAlt className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/function-errors",
          label: "Function Errors",
          icon: <FaExclamationTriangle className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/task-failures",
          label: "Task Failures",
          icon: <FaExclamationTriangle className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/audience-health",
          label: "Audience Health",
          icon: <FaExclamationTriangle className="w-4 h-4 text-gray-300" />,
        },
        {
          to: "/integration-health",
          label: "Integration Health",
          icon: <FaHeartbeat className="w-4 h-4 text-gray-300" />,
        },
      ],
    },
  ];

  return (
    <nav className={`flex flex-col bg-gray-900 text-white transition-all duration-300 ${
      isCollapsed ? 'w-16 px-2' : 'w-64 px-3'
    } py-4 h-screen overflow-hidden`}>
      {/* Header with Toggle */}
      <div className="flex items-center justify-between mb-6">
        {!isCollapsed && (
          <Link to="/statistics" className="text-white no-underline font-bold text-xl">
            Taaly
          </Link>
        )}
        <button
          onClick={onToggle}
          className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? <FaBars className="w-4 h-4 text-gray-300" /> : <FaTimes className="w-4 h-4 text-gray-300" />}
        </button>
      </div>

      {/* Navigation Sections */}
      {(isAdmin || isEmployee) && (
         <div className="flex flex-col gap-1 text-sm flex-1 overflow-y-auto sidebar-scroll pr-1">
           {/* For employees, only show Analytics section */}
           {(isEmployee ? navigationSections.filter(section => section.id === "analytics") : navigationSections).map((section) => (
             <div key={section.id} className="mb-2">
               {/* Section Header - Only show when sidebar is expanded */}
               {!isCollapsed && (
                 <button
                   onClick={() => toggleSection(section.id)}
                   className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors group"
                 >
                   <div className="flex items-center">
                     <span className="mr-3 group-hover:text-white transition-colors">
                       {React.cloneElement(section.icon, {
                         className: `w-4 h-4 text-gray-300 group-hover:text-white`
                       })}
                     </span>
                     <span className="font-medium text-gray-300 text-xs uppercase tracking-wider group-hover:text-white transition-colors">
                       {section.title}
                     </span>
                   </div>
                   <span className="text-gray-400">
                     {expandedSections[section.id] ? (
                       <FaChevronDown className="w-3 h-3 text-gray-400" />
                     ) : (
                       <FaChevronRight className="w-3 h-3 text-gray-400" />
                     )}
                   </span>
                 </button>
               )}

              {/* Section Items */}
              {(!isCollapsed && expandedSections[section.id]) && (
                <div className="ml-4 mt-1 space-y-1">
                  {section.items.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`flex items-center rounded px-3 py-2 text-white hover:bg-gray-800 no-underline transition-colors group ${
                        isLinkActive(item.to) ? "bg-blue-600 font-semibold" : "text-gray-300"
                      }`}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <span className={`mr-3 transition-colors ${
                        isLinkActive(item.to) ? "text-white" : "text-gray-300 group-hover:text-white"
                      }`}>
                        {React.cloneElement(item.icon, {
                          className: `w-4 h-4 ${isLinkActive(item.to) ? "text-white" : "text-gray-300 group-hover:text-white"}`
                        })}
                      </span>
                      <span className="text-sm">{item.label}</span>
                      {item.to === "/coach-records" && hasUnreadCoachRecordings && (
                        <span className="ml-2 inline-block h-2.5 w-2.5 rounded-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.9)]" />
                      )}
                      {item.to === "/users-reports" && hasUnreadUsersReports && (
                        <span className="ml-2 inline-block h-2.5 w-2.5 rounded-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.9)]" />
                      )}
                    </Link>
                  ))}
                </div>
              )}

              {/* Collapsed mode - show only icons */}
              {isCollapsed && (
                <div className="space-y-1">
                  {section.items.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`flex items-center justify-center rounded px-3 py-2 text-white hover:bg-gray-800 no-underline transition-colors group ${
                        isLinkActive(item.to) ? "bg-blue-600 font-semibold" : "text-gray-300"
                      }`}
                      title={item.label}
                    >
                      <span className="relative">
                        {React.cloneElement(item.icon, {
                          className: `w-4 h-4 ${isLinkActive(item.to) ? "text-white" : "text-gray-300 group-hover:text-white"}`
                        })}
                        {item.to === "/coach-records" && hasUnreadCoachRecordings && (
                          <span className="absolute -right-1.5 -top-1.5 h-2.5 w-2.5 rounded-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.9)]" />
                        )}
                        {item.to === "/users-reports" && hasUnreadUsersReports && (
                          <span className="absolute -right-1.5 -top-1.5 h-2.5 w-2.5 rounded-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.9)]" />
                        )}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="mt-auto pt-4 border-t border-gray-700 flex-shrink-0">
        <button
          onClick={toggleNotifications}
          className={`mb-3 flex w-full items-center rounded-lg px-3 py-2 text-sm text-white no-underline transition-colors ${
            isCollapsed ? "justify-center" : "justify-between"
          } ${notificationsEnabled ? "bg-emerald-700 hover:bg-emerald-600" : "bg-gray-800 hover:bg-gray-700"}`}
          title={notificationsEnabled ? "Disable notifications" : "Enable notifications"}
        >
          {!isCollapsed && <span>{notificationsEnabled ? "Notifications on" : "Notifications off"}</span>}
          {notificationsEnabled ? (
            <IoNotifications className="h-4 w-4 text-white" />
          ) : (
            <IoNotificationsOff className="h-4 w-4 text-gray-300" />
          )}
        </button>
        <button
          onClick={toggleTheme}
          className={`mb-3 flex w-full items-center rounded-lg px-3 py-2 text-sm text-white no-underline transition-colors ${
            isCollapsed ? "justify-center" : "justify-between"
          } ${isDarkMode ? "bg-gray-800 hover:bg-gray-700" : "bg-gray-800 hover:bg-gray-700"}`}
          title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          {!isCollapsed && <span>{isDarkMode ? "Light mode" : "Dark mode"}</span>}
          {isDarkMode ? <FaSun className="h-4 w-4 text-yellow-400" /> : <FaMoon className="h-4 w-4 text-gray-300" />}
        </button>
        <Logout isCollapsed={isCollapsed} />
      </div>
    </nav>
  );
};

export default SideBar;

// {
//   /* <Nav.Link className="nav-link" as={Link} to="/organizations">
//               organizations
//             </Nav.Link> */
// }
// {
//   /* <Nav.Link className="nav-link" as={Link} to="/students">
//               students
//             </Nav.Link>
//             <Nav.Link className="nav-link" as={Link} to="/tutors">
//               tutors
//             </Nav.Link>
//             <Nav.Link className="nav-link" as={Link} to="/vouchers-codes">
//               vouchers codes
//             </Nav.Link> */
// }
// {
//   /* <Nav.Link
//               className="nav-link"
//               as={Link}
//               to="/cancellation-requests"
//             >
//               cancellation requests
//             </Nav.Link> */
// }
// {
//   /* <Nav.Link className="nav-link" as={Link} to="/bookings">
//               bookings
//             </Nav.Link>
//             <Nav.Link className="nav-link" as={Link} to="/applying-tutors">
//               applying tutors
//             </Nav.Link>
//             <Nav.Link className="nav-link" as={Link} to="/sessions">
//               sessions
//             </Nav.Link> */
// }
// {
//   /* <Nav.Link className="nav-link" as={Link} to="/chat-support">
//               chat-support
//             </Nav.Link>
//             <Nav.Link className="nav-link" as={Link} to="/find-for-me-requests">
//               find for me requests
//             </Nav.Link>
//             <Nav.Link className="nav-link" as={Link} to="/available-slots">
//               available slots
//             </Nav.Link>
//             <Nav.Link className="nav-link" as={Link} to="/tutors-transactions">
//               tutors transactions
//             </Nav.Link> */
// }
// {
//   /* <Nav.Link
//               className="nav-link mb-3"
//               as={Link}
//               to="/students-transactions"
//             >
//               students transactions
//             </Nav.Link> */
// }

//  {/* <Nav.Link className="nav-link" as={Link} to="/students">
//               students
//             </Nav.Link>
//             <Nav.Link className="nav-link" as={Link} to="/tutors">
//               tutors
//             </Nav.Link> */}
